import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@core-ui/Components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@core-ui/Components/ui/select';

interface CompactPaginationProps {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  rowsPerPageOptions?: number[];
  className?: string;
}

/** Vertically stacked pagination — fits in narrow columns without wrapping. */
const CompactPagination: React.FC<CompactPaginationProps> = ({
  totalItems,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  rowsPerPageOptions = [5, 10, 15],
  className = '',
}) => {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalPages;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex items-center justify-between text-xs">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isFirst}
          onClick={() => {
            onPageChange(currentPage - 1);
          }}
          className="h-7 px-2"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {t('paginationControls.Previous', 'Previous')}
        </Button>

        <span className="text-muted-foreground tabular-nums">
          {currentPage} / {totalPages}
        </span>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isLast}
          onClick={() => {
            onPageChange(currentPage + 1);
          }}
          className="h-7 px-2"
        >
          {t('paginationControls.Next', 'Next')}
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{t('paginationControls.Rows per page', 'Rows per page')}:</span>
        <Select
          value={String(pageSize)}
          onValueChange={(value) => {
            onPageSizeChange(Number(value));
          }}
        >
          <SelectTrigger className="h-7 w-16 px-2 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {rowsPerPageOptions.map((n) => (
              <SelectItem key={n} value={String(n)} className="text-xs">
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default CompactPagination;
